import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SkeletonLoader from './SkeletonLoader';
import MarkdownContent from '../common/MarkdownContent';

const { FiArchive, FiRotateCcw, FiTrash2, FiCalendar, FiFlag, FiTag } = FiIcons;

const ArchivedTaskItem = ({ task, project, onUnarchive, onDelete, tags = [], t }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'medium':
        return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'text-green-500 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      default:
        return 'text-slate-500 bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return t('taskForm.priorityHigh');
      case 'medium':
        return t('taskForm.priorityMedium');
      case 'low':
        return t('taskForm.priorityLow');
      default:
        return priority;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const projectColor = project?.color || '#3b82f6';
  const taskTags = task.tags?.map(tagId => tags.find(t => t.id === tagId)).filter(Boolean) || [];

  return (
    <motion.div
      layout
      className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60 opacity-75"
      style={{ borderLeft: `4px solid ${projectColor}` }}
    >
      <div className="flex items-start gap-4">
        {/* Archive Icon */}
        <div className="mt-1">
          <SafeIcon icon={FiArchive} className="text-2xl text-slate-400" />
        </div>

        {/* Task Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
            <h3 className="text-lg font-semibold text-slate-500 dark:text-slate-400 line-through">
              {task.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {/* Priority Badge */}
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getPriorityColor(
                  task.priority
                )}`}
              >
                <SafeIcon icon={FiFlag} className="text-xs" />
                {getPriorityLabel(task.priority)}
              </span>
            </div>
          </div>

          {task.description && (
            <MarkdownContent
              text={task.description}
              className="text-slate-500 dark:text-slate-500 mb-3 break-words overflow-wrap-anywhere line-through"
            />
          )}

          {/* Tags */}
          {taskTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {taskTags.map(tag => (
                <span
                  key={tag.id}
                  className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-full text-xs flex items-center gap-1"
                >
                  <SafeIcon icon={FiTag} className="text-xs" />
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-y-2">
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 dark:text-slate-500">
              {/* Archived Date */}
              {task.archivedAt && (
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiArchive} />
                  <span>{t('archive.archivedOn', { date: formatDate(task.archivedAt) })}</span>
                </div>
              )}

              {/* Original Due Date */}
              {task.dueDate && (
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiCalendar} />
                  <span>{t('taskForm.dueDate')}: {formatDate(task.dueDate)}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <motion.button
                onClick={() => onUnarchive(task.id)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                title={t('archive.restore')}
              >
                <SafeIcon icon={FiRotateCcw} />
              </motion.button>
              <motion.button
                onClick={() => onDelete(task.id)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title={t('archive.deleteForever')}
              >
                <SafeIcon icon={FiTrash2} />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ArchivedTasksView = ({ tasks, projects, onUnarchive, onDelete, tags, isLoading = false }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortMode, setSortMode] = useState('newest');

  const getProjectForTask = (taskProjectId) => {
    return projects.find(p => p.id === taskProjectId);
  };

  const filteredTasks = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const searchParts = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    const priorityRank = { high: 0, medium: 1, low: 2 };

    return tasks
      .filter(task => {
        if (projectFilter && task.projectId !== projectFilter) return false;

        if (dateFilter !== 'all') {
          if (!task.archivedAt) return false;
          const archivedAt = new Date(task.archivedAt);

          if (dateFilter === 'week' && archivedAt < weekAgo) return false;
          if (dateFilter === 'month' && archivedAt < monthStart) return false;
          if (dateFilter === 'year' && archivedAt < yearStart) return false;
        }

        if (searchParts.length === 0) return true;

        const tagNames = (task.tags || [])
          .map(tagId => tags.find(tag => tag.id === tagId)?.name || '')
          .join(' ');
        const searchable = `${task.title || ''} ${task.description || ''} ${tagNames}`.toLowerCase();
        return searchParts.every(part => searchable.includes(part));
      })
      .sort((a, b) => {
        if (sortMode === 'oldest') {
          return new Date(a.archivedAt || 0) - new Date(b.archivedAt || 0);
        }

        if (sortMode === 'priority') {
          return (priorityRank[a.priority] ?? 3) - (priorityRank[b.priority] ?? 3);
        }

        return new Date(b.archivedAt || 0) - new Date(a.archivedAt || 0);
      });
  }, [dateFilter, projectFilter, searchQuery, sortMode, tags, tasks]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-1">
          <SafeIcon icon={FiArchive} className="text-2xl text-slate-600 dark:text-slate-400" />
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('archive.title')}</h1>
        </div>
        <p className="text-slate-600 dark:text-slate-400">
          {t('archiveFilters.results', { count: filteredTasks.length })}
        </p>
      </div>

      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('archiveFilters.searchPlaceholder')}
          className="md:col-span-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-white"
        />
        <select
          value={projectFilter}
          onChange={(event) => setProjectFilter(event.target.value)}
          className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-white"
        >
          <option value="">{t('archiveFilters.projectAll')}</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>{project.name}</option>
          ))}
        </select>
        <select
          value={dateFilter}
          onChange={(event) => setDateFilter(event.target.value)}
          className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-white"
        >
          <option value="all">{t('archiveFilters.dateAll')}</option>
          <option value="week">{t('archiveFilters.dateWeek')}</option>
          <option value="month">{t('archiveFilters.dateMonth')}</option>
          <option value="year">{t('archiveFilters.dateYear')}</option>
        </select>
        <select
          value={sortMode}
          onChange={(event) => setSortMode(event.target.value)}
          className="md:col-start-4 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-white"
        >
          <option value="newest">{t('archiveFilters.sortNewest')}</option>
          <option value="oldest">{t('archiveFilters.sortOldest')}</option>
          <option value="priority">{t('archiveFilters.sortPriority')}</option>
        </select>
      </div>

      {isLoading ? (
        <SkeletonLoader variant="task" count={5} />
      ) : filteredTasks.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 shadow-soft border border-slate-200/60 dark:border-slate-700/60">
            <div className="text-6xl mb-4">
              <SafeIcon icon={FiArchive} className="mx-auto text-slate-300 dark:text-slate-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">{t('archive.empty')}</h3>
            <p className="text-slate-600 dark:text-slate-400">
              {t('archive.emptyDescription')}
            </p>
          </div>
        </motion.div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {filteredTasks.map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <ArchivedTaskItem
                  task={task}
                  project={getProjectForTask(task.projectId)}
                  onUnarchive={onUnarchive}
                  onDelete={onDelete}
                  tags={tags}
                  t={t}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ArchivedTasksView;
